import React from "react";

export interface DayColumn {
  key: string;
  /** Weekday plus day of month, already formatted for the active language */
  label: string;
  /** Travels finished that day; `0` means the fleet did not operate */
  travelsCount: number;
  /** Height driver of the bar; `null` when the day has nothing to measure */
  barValue: number | null;
  /** Already formatted — a dash when there is no value to draw */
  barLabel: string;
  /** Value of the ranking indicator on that day, already formatted */
  metricLabel: string;
  hasMetric: boolean;
  /** Invoice integration has not caught up with this day yet */
  isConsolidating: boolean;
  title: string;
}

interface DailySeriesChartProps {
  days: DayColumn[];
  footText: string;
  consolidatingMark: string;
  consolidatingLegend: string;
  emptyText: string;
}

/**
 * Day-by-day series of the period.
 *
 * Days that are still consolidating are hatched instead of solid. Without that mark the chart shows
 * consumption per m³ shooting up at the end of the week, which is not true: the invoices of the last
 * one or two days have simply not arrived yet, so the volume — the denominator — is still short.
 *
 * The series carries EVERY day of the period, including the ones with no operation. A Sunday that is
 * simply dropped leaves a seven-day week drawn with six bars and nobody notices the gap, so a day
 * without operation keeps its slot and its label and draws a bar of height zero.
 */
const DailySeriesChart: React.FC<DailySeriesChartProps> = ({
  days, footText, consolidatingMark, consolidatingLegend, emptyText
}) => {

  if (days.length === 0) return <div className="panel-empty">{emptyText}</div>;

  const highest = Math.max(...days.map((day) => day.barValue ?? 0), 0) || 1;
  const hasConsolidating = days.some((day) => day.isConsolidating);

  return (
    <>
      <div className="days">
        {days.map((day) => (
          <div className="day" key={day.key} title={day.title}>
            <span className={day.barValue === null ? "day-value no-value" : "day-value"}>{day.barLabel}</span>
            <span
              className={[
                "day-bar",
                day.isConsolidating ? "consolidating" : "",
                day.barValue === null || day.barValue <= 0 ? "empty" : ""
              ].filter(Boolean).join(" ")}
              style={{ height: `${((day.barValue ?? 0) / highest) * 100}%` }}
            />
            <span className="day-label">
              {day.label}
              {day.isConsolidating && <span className="consolidating-mark">{consolidatingMark}</span>}
            </span>
          </div>
        ))}
      </div>
      <div className="day-metrics">
        {days.map((day) => (
          <div key={`metric-${day.key}`} style={{ color: day.hasMetric ? "var(--ink)" : "var(--muted)" }}>
            {day.metricLabel}
          </div>
        ))}
      </div>
      <div className="day-foot">{footText}</div>
      {hasConsolidating && (
        <div className="day-foot legend-consolidating">
          <i />
          {consolidatingLegend}
        </div>
      )}
    </>
  );
};

export default DailySeriesChart;
